<script setup lang="ts">
// Opens the list of scans the Perfusion Companion app has uploaded for a case (ENG-7381).
// Distinct from PerfusionScanButton, which hands the app a QR code to capture a *new* scan.

import { computed, ref, type PropType } from 'vue';
import { ButtonType, CustomButton } from '@/components/common/';
import type { CaseModel } from '@/models';
import { useStudyStore } from '@/stores/study';
import PerfusionScansPanel from './PerfusionScansPanel.vue';

const props = defineProps({
  caseModel: {
    type: Object as PropType<CaseModel>,
    required: true
  },
  showText: {
    type: Boolean,
    default: false
  }
});

const studyStore = useStudyStore();
const showScansPanel = ref(false);

const pumpCenterId = computed(
  () => studyStore.selectedStudyId ?? studyStore.selectedStudy?.identifier ?? ''
);

const openPanel = (event: MouseEvent) => {
  event.stopPropagation();
  showScansPanel.value = true;
};
</script>

<template>
  <div class="perfusion-scans-root" @click.stop>
    <CustomButton :icon="{ icon: 'monitor-waveform', style: 'far' }"
      :type="ButtonType.ICON_ONLY"
      tooltip="Perfusion scans"
      v-if="!showText"
      @onClick="openPanel"/>

    <CustomButton :icon="{ icon: 'monitor-waveform', style: 'far' }"
      :type="ButtonType.OUTLINED"
      text="Scans"
      v-if="showText"
      @onClick="openPanel"/>

    <PerfusionScansPanel :visible="showScansPanel"
      :pump-center-id="pumpCenterId"
      :perfusion-id="props.caseModel.identifier"
      :perfusion-label="props.caseModel.label"
      @onClose="showScansPanel = false"/>
  </div>
</template>

<style scoped lang="scss">
.perfusion-scans-root {
  display: inline-flex;
  align-items: center;
}

:deep(.icon-only) {
  padding: unset;
  min-width: unset;
}
</style>
